import { Injectable, NgZone, Inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { createClient, Entry, Asset, ChainModifiers } from 'contentful';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../enviroments/environment';
import { LandingPage } from '../_models/landing-page.model';
import { ContentfulLivePreview, ContentfulSubscribeConfig } from '@contentful/live-preview';
import { marked } from 'marked';


interface ContentfulUpdate {
  sys: {
    id: string;
    type: 'Entry' | 'Asset';
    contentTypeId?: string;
  };
  fields: any;
}

const CONTENTFUL_ENV_STATE_KEY = makeStateKey<string>('contentful-env');

@Injectable({
  providedIn: 'root'
})
export class ContentfulService {
  private apiUrl = 'http://localhost:3000/api';
  private client: ReturnType<typeof createClient>;
  private readonly contentfulEnvironment: string;

  private previewClient: any;
  private livePreview?: ContentfulLivePreview;
  public contenfulSubConfig?: ContentfulSubscribeConfig;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.contentfulEnvironment = this.resolveContentfulEnvironment();

    if (!isPlatformBrowser(this.platformId)) {
      this.transferState.set(CONTENTFUL_ENV_STATE_KEY, this.contentfulEnvironment);
    }

    const runtimeHost = this.getRuntimeHostname() || 'unknown';
    const runtimeTarget = isPlatformBrowser(this.platformId) ? 'browser' : 'server';
    console.log(`[Contentful] resolved environment: ${this.contentfulEnvironment} (target: ${runtimeTarget}, host: ${runtimeHost})`);

    this.client = createClient({
      space: environment.contentful.spaceId,
      accessToken: environment.useContentfulPreview
        ? environment.contentful.previewAccessToken
        : environment.contentful.accessToken,
      host: environment.useContentfulPreview
        ? 'preview.contentful.com'
        : 'cdn.contentful.com',
      environment: this.contentfulEnvironment
    });
  }

  getFormFlow(formId: string): Promise<Entry<any>> {
    return this.client.getEntry(formId, {
      include: 5,
    });
  }

  getEntry(entryID: string): Promise<Entry<any>> {
    return this.client.getEntry(entryID, {
      include: 5,
    });
  }

  async getLandingPage(id: string | null): Promise<Entry<LandingPage>> {
    return this.client.getEntry<LandingPage>((id ? id : ''), {
      
      include: 5, // Resuelve hasta x niveles de Entrys vinculadas
    });    
    
  }

  async getAsset(id: string | null) : Promise<Asset<undefined, string>> {
    return this.client.getAsset((id ? id : ''), {
      
    });
  }



  submitFormResponse(data: any) {
    return this.http.post(`${this.apiUrl}/submit-form`, { formData: data });
  }

  markdownToHTML(md: string) {
    return marked(md);
  }

  async getLivePreviewEntry(id: string): Promise<any> {
    const entry = await this.client.getEntry(id, { include: 5 });

    // Generar etiquetas para Inspector Mode
    const inspectorTags = ContentfulLivePreview.getProps({
      entryId: id,
      fieldId: 'fields' // Campo raíz o específico
    });



    return {
      entry,
      inspectorTags
    };
  }
  private isContentfulUpdate(data: unknown): data is ContentfulUpdate {
    return !!(
      typeof data === 'object' &&
      data !== null &&
      'sys' in data &&
      'fields' in data
    );
  }

  private resolveContentfulEnvironment(): string {
    const fromWindow = this.getWindowEnvValue();
    if (fromWindow) {
      return fromWindow;
    }

    const fromTransferState = this.getTransferStateEnvValue();
    if (fromTransferState) {
      return fromTransferState;
    }

    const fromProcess = this.getRuntimeEnvValue();
    if (fromProcess) {
      return fromProcess;
    }

    const host = this.getRuntimeHostname();
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'development';
    }

    return 'master';
  }

  private getWindowEnvValue(): string | null {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return null;
    }

    const win = window as any;
    const envValue = typeof win.__CONTENTFUL_ENV__ === 'string'
      ? win.__CONTENTFUL_ENV__
      : null;

    if (!envValue) {
      return null;
    }

    const normalized = envValue.toLowerCase();
    if (normalized === 'master' || normalized === 'development') {
      return normalized;
    }

    return null;
  }

  private getTransferStateEnvValue(): string | null {
    const envValue = this.transferState.get<string | null>(CONTENTFUL_ENV_STATE_KEY, null);
    if (!envValue) {
      return null;
    }

    const normalized = envValue.toLowerCase();
    if (normalized === 'master' || normalized === 'development') {
      return normalized;
    }

    return null;
  }

  private getRuntimeEnvValue(): string | null {
    const envValue = typeof process !== 'undefined'
      ? process.env?.['CONTENTFUL_ENV']
      : undefined;

    if (!envValue) {
      return null;
    }

    const normalized = envValue.toLowerCase();
    if (normalized === 'master' || normalized === 'development') {
      return normalized;
    }

    return null;
  }

  private getRuntimeHostname(): string {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      return window.location.hostname.toLowerCase();
    }

    return this.document?.location?.hostname?.toLowerCase() ?? '';
  }

}

